import React, { useEffect, useState, useCallback } from "react";
import { Redirect, Route } from "react-router-dom";
import Header from "../Components/common/Header";
import Sidebar from "../Components/common/sidebar";
import { useDispatch, useSelector } from "react-redux";
import CompleteProfileModal from "../Components/profile/profileNotValidModal";
import GrabOrderPopup from "../Components/common/Grab_Order_Popup";
import ErrorBoundary from "../Components/ErrorBoundary";
import MainLoader from "../Components/common/Loader/PageLoader";
import NotificationPermissionPopup from "../Components/common/NotificationPermissionPopup";
import { useAlert } from "react-alert";
import { useNotification } from "../Components/common/OrderLost/NotificationContext";
import { Actions } from "../Actions/Actions";
import { setupMessageListener } from "../utilities/fcmTokenManager"
import { loadSellerMeIfNeeded } from "../Actions/bootstrapSellerMe";
import {
  ensureVendorFrozenCheck,
  resetVendorFrozenCheck,
} from "../Actions/bootstrapVendorFrozenCheck";

// Status constants
const STATUS_TYPES = {
  ACTIVE: 'seller fetched',
  TEMPORARILY_INACTIVE: 'seller temporarily inactive',
  NOT_ACTIVATED: 'Seller Not Activated',
  NOT_FOUND: 'Seller Not Found'
};

export const ProtectedRoutes = ({ component: Component, ...restOfProps }) => {
  const { isProfileOk } = useSelector(state => state.profileReducer);
  // SL-890
  const actionReducer = useSelector(state => state.ActionReducer);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirectToFrozen, setShouldRedirectToFrozen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const dispatch = useDispatch();
  const alert = useAlert();
  const { addOrderLostNotification } = useNotification();

  // Sl-828 — profile completeness uses same `me` payload as bootstrapSellerMe (single API)

  const checkAuthentication = useCallback(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setIsAuthenticated(false);
      return false;
    }

    setIsAuthenticated(true);
    return true;
  }, []);
  // Check vendor frozen status
  const checkVendorFrozen = useCallback(async () => {
    try {
      const response = await ensureVendorFrozenCheck(dispatch);

      if (!response) {
        setShouldRedirectToFrozen(false);
        return;
      }

      // Handle frozen states
      if (response.message === STATUS_TYPES.TEMPORARILY_INACTIVE) {
        // Store freeze details for the frozen page
        if (response.data) {
          const freezeData = {
            tmpStatus: response.data.tmpStatus,
            daysSinceLastOrder: response.data.daysSinceLastOrder,
            hasPendingRequest: response.data.hasPendingRequest,
            pendingRequest: response.data.pendingRequest
          };
          localStorage.setItem('freezeDetails', JSON.stringify(freezeData));
        }
        setShouldRedirectToFrozen(true);
        return;
      }

      // Handle active state
      if (response.message === STATUS_TYPES.ACTIVE) {
        // Update localStorage with latest data
        if (response.data) {
          localStorage.setItem('vendorTmpStatus', JSON.stringify({
            tmpStatus: response.data.tmpStatus || false
          }));
        }

        // Clear any cached frozen data
        localStorage.removeItem('freezeDetails');
        setShouldRedirectToFrozen(false);
        return;
      }

      // Handle not activated state
      if (response.message === STATUS_TYPES.NOT_ACTIVATED) {
        if (response.data) {
          localStorage.setItem('vendorData', JSON.stringify(response.data));
        }
        setShouldRedirectToFrozen(false);
        return;
      }

      // Handle not found state  
      if (response.message === STATUS_TYPES.NOT_FOUND) {
        setShouldRedirectToFrozen(false);
        setIsAuthenticated(false); // This will trigger redirect to login
        return;
      }

      // Default case
      setShouldRedirectToFrozen(false);

    } catch (error) {
      // On error, assume vendor is active
      setShouldRedirectToFrozen(false);
    }
  }, []);

  // Main authentication and status check
  const performAuthCheck = useCallback(async () => {
    try {
      setIsLoading(true);
      resetVendorFrozenCheck(dispatch);

      // First check authentication
      const isAuth = checkAuthentication();
      if (!isAuth) {
        setIsLoading(false);
        return;
      }

      try {
        await loadSellerMeIfNeeded(dispatch);
      } catch (err) {
        console.log(err);
      }

      // Special handling for frozen page
      if (restOfProps.path === "/frozen") {
        await checkVendorFrozen();

        // If vendor is active, redirect away from frozen page
        if (!shouldRedirectToFrozen) {
          window.location.href = "/home";
          return;
        }

        setIsLoading(false);
        return;
      }

      // For all other routes, check frozen status
      await checkVendorFrozen();

    } catch (error) {
      // On error, assume vendor is active
      setShouldRedirectToFrozen(false);
    } finally {
      setIsLoading(false);
    }
  }, [checkAuthentication, checkVendorFrozen, restOfProps.path, shouldRedirectToFrozen]);


  // Run auth check on mount
  useEffect(() => {
    performAuthCheck();
  }, []);

  // Set up message listener for foreground notifications
  useEffect(() => {
    if (!isAuthenticated) return;
    if(shouldRedirectToFrozen) return;

    const unsubscribeMessages = setupMessageListener((payload) => {
      alert.show(payload.data.title);
      if (localStorage.getItem("token")) {
        switch (payload?.data?.type) {
          case "GRAB_ORDER":
            Actions.updateGrabOrderPopupState(dispatch, {
              visible: true,
              data: payload.data
            });
            break;
            // SL-1158 (5-5-26)
          // case "LOST_ORDER":
          //   const orderData = payload.data;
          //   addOrderLostNotification({
          //     productName: orderData?.productName,
          //     winningVendor: orderData?.vendorName,
          //     winningPrice: orderData?.otherSellerPrice,
          //     vendorPrice: orderData?.purchasePrice,
          //     image: orderData?.image || '',
          //     product_id: orderData?.productId,
          //     _id: orderData?.lostId || '',
          //     SKU: orderData?.SKU || '',
          //     urlKey: orderData?.urlKey,
          //     attributeValue: orderData?.attributeValue,
          //     attributes: orderData?.attributes,
          //     lowestPrice: orderData?.lowestPrice,
          //     from: "notification"
          //   });
          //   break;
          default:
            break;
        }
      }
    });

    return () => {
      unsubscribeMessages();
    };
  }, [isAuthenticated,shouldRedirectToFrozen,  dispatch, alert, addOrderLostNotification]);


  // Show loading spinner
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <MainLoader/>
      </div>
    );
  }


  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Redirect to="/" />;
  }

  // Redirect to frozen page if seller is frozen/inactive
  if (shouldRedirectToFrozen && restOfProps.path !== "/frozen") {
    return <Redirect to="/frozen" />;
  }

  if (localStorage.getItem("token")) {
    return (
      <Route
        {...restOfProps}
        render={(props) => (
          <>
            <div className="row align-items-start">
              <Sidebar />
              <div
                className="rightbar"
                style={{ padding: '0px 7px', marginLeft: "250px", width: "calc(100vw - 227px)", backgroundColor: "#ecedef" }}
              >
                <Header />
                {
                  !isProfileOk && (restOfProps?.path !== "/profile") ? <><CompleteProfileModal /></> : <div
                    style={{
                      minHeight: "500px",
                      // background:'#fff'
                    }}
                  >
                    <div style={{ minHeight: "500px", backgroundColor: "#fff" }}>

                      <ErrorBoundary>
                        <NotificationPermissionPopup />
                         <GrabOrderPopup show={actionReducer.grabOrderPopupState.visible} />
                        <Component {...props} />
                      </ErrorBoundary>
                    </div>
                  </div>
                }

              </div>
            </div>
          </>
        )}
      />
    );
  } else {
    return <Redirect to="/" />;
  }
};
